import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faQuestion,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'

import OperationsIcon from '../RecommendationTabs/icons/OperationsIcon'
import PerformanceIcon from '../RecommendationTabs/icons/PerformanceIcon'
import CostIcon from '../RecommendationTabs/icons/CostIcon'
import ReliabilityIcon from '../RecommendationTabs/icons/ReliabilityIcon'
import SecurityIcon from '../RecommendationTabs/icons/SecurityIcon'
import TrainingIcon from '../RecommendationTabs/icons/TrainingIcon'
import Heading from '../Heading/Heading'
import Tooltip from '../Tooltip/Tooltip'

const Box = styled(Link)`
  position: relative;
  border: 2px solid var(--color-bg-sidebar);
  border-radius: 18px;
  background-color: var(--color-bg-sidebar);
  padding: 28px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 152px;
  width: 100%;

  @media only screen and (max-width: 920px) {
    max-width: 200px;
  }
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:not(:last-child) {
    margin-right: 28px;
  }

  @media only screen and (max-width: 1400px) {
    &:not(:last-child) {
      margin-right: 0;
    }
  }

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  @media only screen and (max-width: 370px) {
    padding: 20px 5px;
  }
`

const Col = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`
const Row = styled.div`
  display: flex;
  align-items: center;
`

const getColor = priority => {
  if (priority === 'high') return '#ff4140'
  if (priority === 'medium') return '#DCA427'
  if (priority === 'low') return '#0EA023'
}

const IconBox = styled.div`
  color: ${p => getColor(p.priority)};
  border: 8px solid ${p => getColor(p.priority)};
  height: 80px;
  width: 80px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 30px;
    width: auto;
    fill: #fff;
  }
`

const StatusBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  height: 24px;
  width: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => getColor(p.priority)};

  svg {
    font-size: 12px;
    ${p => p.priority === 'high' && 'transform: translate(0.25px, -0.5px);'}
  }
`

const Title = styled.span`
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 900;
  margin: 23px 0 9px;
`

const Desc = styled.span`
  color: var(--color-text-secondary);
  font-size: 12px;
`

const Grid = styled.div`
  display: flex;
  margin-top: 35px;

  @media only screen and (max-width: 1400px) {
    display: grid;
    grid-gap: 28px;
    grid-template-columns: repeat(3, 152px);
  }

  @media only screen and (max-width: 920px) {
    grid-template-columns: repeat(2, 1fr);
    justify-items: center;
  }

  @media only screen and (max-width: 555px) {
    grid-gap: 14px;
  }

  @media only screen and (max-width: 370px) {
    grid-gap: 5px;
    grid-template-columns: repeat(2, 1fr);
  }
`

const Info = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  background-color: var(--color-text-secondary);
  width: 20px;
  height: 20px;
  border-radius: 50%;
`

const summaryList = [
  {
    Icon: SecurityIcon,
    title: 'Security',
    url: '/recommendations/security',
  },
  {
    Icon: OperationsIcon,
    title: 'Operations',
    url: '/recommendations/operations',
  },
  {
    Icon: ReliabilityIcon,
    title: 'Reliability',
    url: '/recommendations/reliability',
  },
  {
    Icon: PerformanceIcon,
    title: 'Performance',
    url: '/recommendations/performance',
  },
  {
    Icon: CostIcon,
    title: 'Cost',
    url: '/recommendations/cost',
  },
  {
    Icon: TrainingIcon,
    title: 'Training',
    url: '/recommendations/training',
  },
]

const RecommendationSummary = ({ listRecommendations }) => {
  const summary = useMemo(() => {
    return listRecommendations.items.reduce(
      (res, obj) => {
        const key = obj.awsPillar?.toLowerCase()

        res[key].count = res[key].count + 1

        if (obj.highPriority) {
          res[key].high = true
        }

        return res
      },
      {
        security: {
          count: 0,
          high: false,
        },
        cost: {
          count: 0,
          high: false,
        },
        reliability: {
          count: 0,
          high: false,
        },
        operations: {
          count: 0,
          high: false,
        },
        performance: {
          count: 0,
          high: false,
        },
        training: {
          count: 0,
          high: false,
        },
      },
    )
  }, [listRecommendations])

  return (
    <Col>
      <Row>
        <Heading style={{ marginRight: '16px' }}>
          Recommendations summary
        </Heading>
        <Tooltip
          className='summary-tooltip'
          html={true}
          content="The pillars are derived from AWS's 
                   Well Architected Framework and colored 
                   based on Caylent's expert guidance on the 
                   criticality of recommendations in that area.
                   <br />
                   <br />
                   <a class='tooltip-link' href='#'>Learn More</a>"
        >
          <Info>
            <FontAwesomeIcon icon={faQuestion} color='#e3e9ef' size='sm' />
          </Info>
        </Tooltip>
      </Row>
      <Grid>
        {summaryList.map(({ Icon, title, url }) => {
          let priority
          let asideIcon

          if (summary[title.toLowerCase()].high) {
            priority = 'high'
            asideIcon = faExclamationTriangle
          } else if (summary[title.toLowerCase()].count > 3) {
            priority = 'medium'
            asideIcon = faQuestion
          } else {
            priority = 'low'
            asideIcon = faCheck
          }

          return (
            <Box to={url} key={url} className='rec-summary-box'>
              <StatusBadge priority={priority}>
                <FontAwesomeIcon
                  icon={asideIcon}
                  color='#fff'
                  size='xs'
                />
              </StatusBadge>
              <IconBox priority={priority}>
                <Icon />
              </IconBox>
              <Title>{title}</Title>
              <Desc>{`${summary[title.toLowerCase()].count
                } Recommendations`}</Desc>
            </Box>
          )
        })}
      </Grid>
    </Col>
  )
}

export default RecommendationSummary
